import { html } from '../lib/html.js';
import { toast } from '../lib/ui.js';
import { UPCOMING_STATUS, answerText, fieldText, fmtDay, fmtKm, meterLabel, meterUnit, nextText } from '../services/maintenances.js';

/**
 * Pedaços de tela das manutenções, usados pelo dono (tela do veículo) e pela oficina.
 * Padrão visual: título de seção fora do painel + painel com linhas (ver "painéis" no app.css).
 */

/** Selo de status (Vencida / Em breve / Em dia…). */
export const pill = (color, label) => html`<span class="pill ${color}">${label}</span>`;

/** Título de seção, com ação opcional à direita. */
export const sectionTitle = (title, action = '') => html`<div class="sec-title"><h2>${title}</h2>${action}</div>`;

/** Situação das revisões (valor, cor e ícone) a partir da lista de próximas. */
function revState(upcoming) {
  const overdue = upcoming.filter((u) => u.status === 'OVERDUE').length;
  const soon = upcoming.filter((u) => u.status === 'SOON').length;
  return overdue
    ? { value: overdue === 1 ? '1 vencida' : `${overdue} vencidas`, cls: 'danger', icon: 'alert-circle-outline' }
    : soon
      ? { value: soon === 1 ? '1 em breve' : `${soon} em breve`, cls: 'warning', icon: 'time-outline' }
      : upcoming.length
        ? { value: 'Em dia', cls: 'success', icon: 'checkmark-circle-outline' }
        : { value: '—', cls: '', icon: 'calendar-outline' };
}

/**
 * Km atual + situação das revisões, lado a lado.
 * compact: ícone na linha do rótulo e menos altura (tela do veículo do dono).
 */
export function stats({ currentKm, upcoming, kmSource = null, kmDate = null, kmMine = false }, { kmAction = '', compact = false } = {}) {
  const when = kmDate ? ` em ${fmtDay(kmDate).slice(0, 5)}` : '';
  const fem = meterUnit() === 'HOURS' ? 'Informado' : 'Informada';
  const kmCaption = currentKm === null ? 'Sem registro ainda' : kmSource === 'OWNER' ? `${kmMine ? `${fem} por você` : `${fem} no app`}${when}` : `Última manutenção${when}`;
  const rev = revState(upcoming);
  const label = (icon, text) => (compact ? html`<span class="stat-label"><ion-icon name="${icon}"></ion-icon>${text}</span>` : html`<ion-icon name="${icon}"></ion-icon><span class="stat-label">${text}</span>`);
  return html`
    <div class="stats ${compact ? 'compact' : ''}">
      <div class="stat">
        ${label(meterUnit() === 'HOURS' ? 'tc-hourmeter' : 'speedometer-outline', meterLabel())}
        <strong class="stat-value">${currentKm === null ? '—' : fmtKm(currentKm)}</strong>
        <span class="stat-caption">${kmCaption}</span>
        ${kmAction}
      </div>
      <div class="stat ${rev.cls}">
        ${label(rev.icon, 'Revisões')}
        <strong class="stat-value">${rev.value}</strong>
        <span class="stat-caption">${upcoming.length ? `${upcoming.length} prevista${upcoming.length > 1 ? 's' : ''}` : 'Nenhuma prevista'}</span>
      </div>
    </div>`;
}

/**
 * Próximas revisões (mais urgentes primeiro, como vêm da API).
 * limit: mostra só as primeiras e um botão "Ver todas" (data-action="upcoming-all").
 */
export function upcomingList(upcoming, { limit = 0, title = true } = {}) {
  if (!upcoming.length) return '';
  const shown = limit ? upcoming.slice(0, limit) : upcoming;
  const more = upcoming.length - shown.length;
  return html`
    ${title ? sectionTitle('Próximas revisões') : ''}
    <div class="panel">
      ${shown.map((u) => {
        const st = u.check && u.status !== 'OK' ? { color: 'warning', label: 'Verificar' } : UPCOMING_STATUS[u.status];
        const left = u.kmLeft !== null && u.kmLeft > 0 ? `Faltam ${fmtKm(u.kmLeft)}` : '';
        return html`
          <div class="row">
            <span class="dot ${st.color}"></span>
            <div class="row-main">
              <span class="row-title">${u.typeName}</span>
              <span class="row-sub">${u.check ? `Pedir para a empresa verificar até ${fmtDay(u.nextDueDate)}` : nextText(u)}${u.estimated && !u.check ? ' · estimado' : ''}</span>
              ${left ? html`<span class="row-meta">${left}</span>` : ''}
            </div>
            ${pill(st.color, st.label)}
          </div>`;
      })}
      ${more > 0 ? html`<button type="button" class="row tappable more-row" data-action="upcoming-all">Ver todas as ${upcoming.length} revisões<ion-icon name="chevron-down"></ion-icon></button>` : ''}
    </div>`;
}

/** Km atual + situação das revisões (dois blocos lado a lado) e a lista de próximas revisões. */
export function summary(data, { kmAction = '' } = {}) {
  return html`${stats(data, { kmAction })}${upcomingList(data.upcoming)}`;
}

/**
 * Histórico: uma linha por manutenção, mais recente primeiro. Tocar abre os detalhes
 * (itens, observações, versões e, para a oficina, "Corrigir").
 * Cada linha diz quem registrou: oficina autorizada (selo verde) ou o dono (dados iniciais, cartão tracejado).
 * @param {any[]} items
 * @param {{ open?: string | null, versions?: Record<string, any[]>, correctHref?: (m: any) => string | null, emptyText?: string, emptyAction?: any, initial?: any[] }} o
 */
export function history(items, { open = null, versions = {}, correctHref = () => null, emptyText, emptyAction = '', initial = [], byYear = false, title = true, legendMode = 'inline', legendOpen = false, count = null, ownerChanges = [] } = {}) {
  if (!items.length && !initial.length) {
    return html`
      ${title ? sectionTitle('Histórico de manutenção') : ''}
      <div class="panel empty-panel">
        <div class="empty-badge"><ion-icon name="construct-outline"></ion-icon></div>
        <strong>Nenhuma manutenção registrada</strong>
        <p>${emptyText ?? 'Quando uma empresa autorizada registrar um serviço, ele aparece aqui com data, km e o que foi trocado.'}</p>
        ${emptyAction}
      </div>`;
  }
  // oficinas e dados iniciais na mesma linha do tempo (mais recente primeiro)
  const rows = timeline(items, initial, ownerChanges);
  const records = rows.filter((r) => !r.change);
  const renderRow = ({ m, d, change }) => (change ? ownerChangeRow(change) : d ? initialRow(d, open) : maintRow(m, { open, versions, href: correctHref(m) }));
  const legendBtn =
    legendMode === 'toggle' && initial.length
      ? html`<button type="button" class="link-btn legend-btn" data-action="legend" aria-expanded="${legendOpen ? 'true' : 'false'}"><ion-icon name="information-circle-outline"></ion-icon>Quem registrou?</button>`
      : '';
  const head = title ? sectionTitle('Histórico de manutenção', html`${legendBtn}<span class="sec-count">${count ?? records.length}</span>`) : legendBtn ? html`<div class="sec-tools">${legendBtn}</div>` : '';
  const showLegend = initial.length && (legendMode === 'inline' || (legendMode === 'toggle' && legendOpen));
  if (!byYear) {
    return html`${head}${legendMode === 'toggle' && showLegend ? legend() : ''}<div class="panel">${rows.map(renderRow)}</div>${legendMode === 'inline' && showLegend ? legend() : ''}`;
  }
  // agrupado por ano: "2026 · 4 registros" (o marco de troca de dono não conta)
  const recs = (list) => list.filter((r) => !r.change).length;
  const years = [];
  for (const r of rows) {
    const y = String(r.day).slice(0, 4);
    if (years.at(-1)?.year !== y) years.push({ year: y, rows: [] });
    years.at(-1).rows.push(r);
  }
  return html`
    ${head}
    ${showLegend ? legend() : ''}
    ${years.map(
      (g) => html`
        <div class="year-head"><strong>${g.year}</strong><span>${recs(g.rows)} ${recs(g.rows) === 1 ? 'registro' : 'registros'}</span></div>
        <div class="panel">${g.rows.map(renderRow)}</div>`,
    )}`;
}

/**
 * Manutenções, dados iniciais e trocas de dono numa linha do tempo só, mais recente primeiro.
 * No mesmo dia, o marco "Mudou de dono" fica depois (abaixo) dos registros daquele dia.
 */
export function timeline(items, initial = [], ownerChanges = []) {
  const key = (r) => `${r.day}${r.change ? '0' : '1'}`;
  return [
    ...items.map((m) => ({ day: m.serviceDate, m })),
    ...initial.map((d) => ({ day: d.recordedDate, d })),
    ...ownerChanges.map((at) => ({ day: String(at).slice(0, 10), change: at })),
  ].sort((a, b) => (key(a) < key(b) ? 1 : key(a) > key(b) ? -1 : 0));
}

const MONTHS = ['jan', 'fev', 'mar', 'abr', 'mai', 'jun', 'jul', 'ago', 'set', 'out', 'nov', 'dez'];
/** Marco da venda no histórico: o que vem abaixo é do(s) dono(s) anterior(es). */
const ownerChangeRow = (at) => {
  const d = new Date(at);
  return html`<div class="owner-change"><ion-icon name="swap-horizontal-outline"></ion-icon><span>Mudou de dono · ${MONTHS[d.getMonth()]}/${d.getFullYear()}</span></div>`;
};

/** Uma manutenção registrada por oficina; tocar abre os detalhes. */
function maintRow(m, { open, versions, href }) {
  const isOpen = open === m.id;
  return html`
    <div class="maint-row ${isOpen ? 'open' : ''}">
      <button type="button" class="row tappable" data-action="toggle" data-id="${m.id}" aria-expanded="${isOpen ? 'true' : 'false'}">
        <span class="row-icon"><ion-icon name="construct-outline"></ion-icon></span>
        <div class="row-main">
          <span class="row-title">${m.items.map((i) => i.typeName).join(', ')}</span>
          <span class="row-sub">${fmtDay(m.serviceDate)} · ${fmtKm(m.km)}${m.corrected ? html` · <span class="corrected">corrigida</span>` : ''}</span>
          <span class="src-tag shop"><ion-icon name="shield-checkmark-outline"></ion-icon><span class="src-name">Empresa autorizada · ${m.workshop?.name ?? 'Empresa'}</span></span>
          ${m.recordedBy || photoCount(m) ? html`<span class="row-meta">${m.recordedBy?.name ?? ''}${photoCount(m) ? html`${m.recordedBy ? ' · ' : ''}<ion-icon name="camera-outline" class="inline-icon"></ion-icon> ${photoCount(m)}` : ''}</span>` : ''}
        </div>
        <ion-icon class="chevron" name="chevron-down"></ion-icon>
      </button>
      ${isOpen
        ? html`
          <div class="maint-detail">
            <ul class="maint-items">
              ${m.items.map((i) => html`
                <li>
                  <span class="row-title">${i.typeName}</span>
                  ${i.description ? html`<span class="row-sub">${i.description}</span>` : ''}
                  ${(i.fields ?? []).map((f) => html`<span class="row-sub item-field"><span>${f.label}:</span> ${fieldText(f)}</span>`)}
                  ${i.nextDueKm !== null || i.nextDueDate
                    ? html`<span class="next">Próxima: ${[i.nextDueKm !== null ? fmtKm(i.nextDueKm) : '', i.nextDueDate ? fmtDay(i.nextDueDate) : ''].filter(Boolean).join(' ou ')}</span>`
                    : ''}
                  ${photoStrip(i)}
                </li>`)}
            </ul>
            ${m.notes ? html`<p class="maint-notes">${m.notes}</p>` : ''}
            ${m.corrected ? html`<p class="hint small">Corrigida em ${new Date(m.lastEditedAt).toLocaleDateString('pt-BR')}: ${m.lastReason ?? ''}</p>` : ''}
            ${versions[m.id] ? versionList(versions[m.id]) : ''}
            ${(m.corrected && !versions[m.id]) || href
              ? html`<div class="detail-actions">
                  ${m.corrected && !versions[m.id] ? html`<ion-button size="small" fill="clear" data-action="versions" data-id="${m.id}">Ver versões anteriores</ion-button>` : ''}
                  ${href ? html`<ion-button size="small" fill="outline" data-go="${href}"><ion-icon slot="start" name="create-outline"></ion-icon>Corrigir</ion-button>` : ''}
                </div>`
              : ''}
          </div>`
        : ''}
    </div>`;
}

/** Dados iniciais informados pelo dono: cartão tracejado com o selo "Informado por você/pelo dono". */
function initialRow(d, open) {
  const id = `init-${d.id}`;
  const isOpen = open === id;
  return html`
    <div class="maint-row owner ${isOpen ? 'open' : ''}">
      <button type="button" class="row tappable" data-action="toggle" data-id="${id}" aria-expanded="${isOpen ? 'true' : 'false'}">
        <span class="row-icon owner"><ion-icon name="person-outline"></ion-icon></span>
        <div class="row-main">
          <span class="row-title">Dados iniciais do veículo</span>
          <span class="row-sub">${fmtDay(d.recordedDate)} · ${fmtKm(d.km)}${d.corrected ? html` · <span class="corrected">corrigidos</span>` : ''}</span>
          <span class="src-tag owner"><ion-icon name="person-outline"></ion-icon>${d.mine ? 'Informado por você' : d.previousOwner ? 'Informado pelo dono anterior' : 'Informado pelo dono'}</span>
        </div>
        <ion-icon class="chevron" name="chevron-down"></ion-icon>
      </button>
      ${isOpen
        ? html`
          <div class="maint-detail">
            ${d.items.length
              ? html`<ul class="maint-items">${d.items.map((i) => html`<li><span class="row-title">${i.typeName}</span><span class="row-sub">Última troca: ${answerText(i)}</span></li>`)}</ul>`
              : html`<p class="hint small">Só a km atual foi informada.</p>`}
            <p class="hint small">Não verificado por empresa. Serve para os lembretes; não é comprovante de serviço.</p>
            ${d.canCorrect
              ? html`<div class="detail-actions"><ion-button size="small" fill="outline" data-action="initial-edit"><ion-icon slot="start" name="create-outline"></ion-icon>Corrigir até ${fmtDay(d.correctUntil)}</ion-button></div>`
              : d.mine
                ? html`<p class="hint small">Correção encerrada: passou o prazo ou uma empresa já registrou serviço.</p>`
                : ''}
          </div>`
        : ''}
    </div>`;
}

/** "Quem registrou?": explica os dois selos do histórico. */
const legend = () => html`
  <div class="panel src-legend">
    <strong>Quem registrou?</strong>
    <p><span class="src-tag shop"><ion-icon name="shield-checkmark-outline"></ion-icon>Empresa</span>Serviço feito e registrado por uma empresa que você autorizou.</p>
    <p><span class="src-tag owner"><ion-icon name="person-outline"></ion-icon>Dono</span>Informado pelo dono ao ativar os lembretes. Não é comprovante de serviço.</p>
  </div>`;

const photoCount = (m) => m.items.reduce((n, i) => n + (i.photos?.length ?? 0), 0);

/** Fotos da peça no detalhe (tocar amplia). Registro sem foto num tipo que aceita foto: "Sem foto". */
function photoStrip(item) {
  const photos = item.photos ?? [];
  if (photos.length) {
    return html`<div class="photo-strip">
      ${photos.map((p) =>
        p.url
          ? html`<button type="button" class="photo-thumb" data-action="photo" data-url="${p.url}" aria-label="Ampliar foto da peça"><img src="${p.url}" alt="Foto da peça" loading="lazy" /></button>`
          : html`<span class="photo-thumb missing"><ion-icon name="camera-outline"></ion-icon></span>`,
      )}
    </div>`;
  }
  return item.photoPolicy === 'OPTIONAL' || item.photoPolicy === 'REQUIRED' ? html`<span class="no-photo"><ion-icon name="camera-outline"></ion-icon>Sem foto</span>` : '';
}

/** Descrição e campos adicionais do item numa linha só: " (Mobil 1; Litros: 4,5)". */
function itemDetails(i) {
  const parts = [i.description, ...(i.fields ?? []).map((f) => `${f.label}: ${fieldText(f)}`)].filter(Boolean);
  return parts.length ? ` (${parts.join('; ')})` : '';
}

function versionList(list) {
  return html`
    <div class="versions">
      ${list
        .filter((v) => !v.current)
        .map(
          (v) => html`
            <div class="version">
              <p><strong>Versão ${v.version}</strong> · ${fmtDay(v.serviceDate)} · ${fmtKm(v.km)}</p>
              <p>${v.items.map((i) => `${i.typeName}${itemDetails(i)}${i.photos?.length ? ` · ${i.photos.length} foto${i.photos.length > 1 ? 's' : ''}` : ''}`).join(', ')}</p>
              ${v.notes ? html`<p>${v.notes}</p>` : ''}
            </div>`,
        )}
    </div>`;
}

/** "Agora não" no cartão dos lembretes: some neste aparelho (Início e veículo); o histórico vazio continua oferecendo. */
const SETUP_LATER = (vid) => `tc.setupLater.${vid}`;
export function setupLater(vid) {
  try {
    return localStorage.getItem(SETUP_LATER(vid)) === '1';
  } catch {
    return false;
  }
}
export function dismissSetup(vid) {
  try {
    localStorage.setItem(SETUP_LATER(vid), '1');
  } catch {
    /* sem armazenamento: o cartão volta na próxima vez */
  }
  toast('Tudo bem. Você pode informar depois pelo histórico do veículo.');
}

/**
 * Cartão "Ative os lembretes de revisão": dono sem dados iniciais e com perguntas liberadas no admin.
 * Botões com data-action="setup-start|setup-later" e data-vid (cada tela trata o clique).
 * @param {{ vid: string, car?: string, questions?: number }} o   car: nome do veículo (Início, com vários veículos)
 */
export const setupCard = ({ vid, car = '', questions }) => html`
  <div class="panel setup-card">
    <div class="setup-head">
      <span class="setup-bell"><ion-icon name="notifications-outline"></ion-icon></span>
      <div>
        <strong>Ative os lembretes de revisão${car ? ` do ${car}` : ''}</strong>
        <p>Conte quando foram as últimas trocas e avisamos antes de cada uma vencer.</p>
      </div>
    </div>
    <div class="setup-meta">
      <span><ion-icon name="time-outline"></ion-icon>Menos de 1 minuto</span>
      ${questions ? html`<span><ion-icon name="checkmark-circle-outline"></ion-icon>${questions + 1} perguntas</span>` : ''}
    </div>
    <div class="setup-actions">
      <ion-button data-action="setup-start" data-vid="${vid}">Começar</ion-button>
      <ion-button fill="clear" data-action="setup-later" data-vid="${vid}">Agora não</ion-button>
    </div>
  </div>`;
